import { makeStyles } from "@material-ui/core";
import * as React from "react";
import CustomModal from "common/components/CustomModal/CustomModal";
import {
	ModalBody,
	ModalHeader,
	ModalPanel,
} from "common/components/CustomModal/Modal/ModalFrame";
import ModalHelper from "common/helpers/modalHelper";
import { GamesEnum, GetCurrentGameId } from "common/constants/Games";
import { toMonthNameDayAndYear } from "common/helpers/dateHelper";
import { Colors } from "styles/Colors";

const useStyles = makeStyles({
	panel: {
		width: 440,
	},
	lead: {
		fontSize: 13,
		lineHeight: "19px",
		margin: "0 0 14px",
	},
	label: {
		color: Colors.pickstop,
		fontSize: 12,
		letterSpacing: 1,
		textTransform: "uppercase",
		margin: "0 0 6px",
	},
	table: {
		width: "100%",
		borderSpacing: 0,
		marginBottom: 16,
		fontSize: 13,
		tableLayout: "fixed",
		"& th:first-child, & td:first-child": {
			width: "40%",
		},
		"& th:last-child, & td:last-child": {
			width: "22%",
		},
	},
	tableLast: {
		marginBottom: 8,
	},
	head: {
		color: Colors.primaryDarker,
		fontSize: 11,
		letterSpacing: 1,
		textTransform: "uppercase",
		fontWeight: "normal",
		textAlign: "left",
		padding: "0 0 4px",
	},
	cell: {
		padding: "4px 0",
		borderTop: "1px solid " + Colors.panelLight,
	},
	award: {
		fontWeight: "bold",
	},
	value: {
		textAlign: "right",
		fontWeight: "bold",
		color: Colors.pickstop,
	},
	foot: {
		fontSize: 12,
		color: Colors.primaryDark,
		lineHeight: "18px",
	},
});

const tiers = [
	{ finish: "Winner", award: "Diamond", points: 50, color: Colors.diamond_light },
	{ finish: "Top 10", award: "Platinum", points: 30, color: Colors.platinum_light },
	{ finish: "Top 10%", award: "Gold", points: 20, color: Colors.gold_light },
	{ finish: "Top 30%", award: "Silver", points: 12, color: Colors.silver_light },
	{ finish: "Top 50%", award: "Bronze", points: 6, color: Colors.bronze_light },
	{ finish: "Everyone else", award: "Wood", points: 2, color: Colors.wood_light },
];

const eventsByGame = {
	[GamesEnum.CounterStrike]: [
		{ event: "Major", award: "Major Trophy", multiplier: 2 },
		{ event: "Cologne, Katowice", award: "Prestige Trophy", multiplier: 1.5 },
		{ event: "S-Tier LAN", award: "Medal", multiplier: 1 },
	],
	[GamesEnum.RocketLeague]: [
		{ event: "Worlds", award: "Globe", multiplier: 2 },
		{ event: "Major", award: "Trophy", multiplier: 2 },
		{ event: "Regional", award: "Medal", multiplier: 1 },
	],
};

export default function SeasonScoreModal({ season }) {
	const styles = useStyles();
	const events =
		eventsByGame[GetCurrentGameId()] ?? eventsByGame[GamesEnum.RocketLeague];

	return (
		<CustomModal>
			<ModalPanel className={styles.panel}>
				<ModalHeader onClose={() => ModalHelper.closeModal()}>
					Season Score
				</ModalHeader>
				<ModalBody>
					<p className={styles.lead}>
						Your season score is everything you have won this season, added up.
						Each event you play awards one trophy, and what it is worth depends
						on where you finished and on how big the event was.
					</p>

					<h5 className={styles.label}>Where you finished</h5>
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.head}>Finish</th>
								<th className={styles.head}>Award</th>
								<th className={styles.head} style={{ textAlign: "right" }}>
									Points
								</th>
							</tr>
						</thead>
						<tbody>
							{tiers.map((t) => (
								<tr key={t.award}>
									<td className={styles.cell}>{t.finish}</td>
									<td
										className={`${styles.cell} ${styles.award}`}
										style={{ color: t.color }}
									>
										{t.award}
									</td>
									<td className={`${styles.cell} ${styles.value}`}>
										{t.points}
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<h5 className={styles.label}>How big the event was</h5>
					<table className={`${styles.table} ${styles.tableLast}`}>
						<thead>
							<tr>
								<th className={styles.head}>Event</th>
								<th className={styles.head}>Award</th>
								<th className={styles.head} style={{ textAlign: "right" }}>
									Multiplier
								</th>
							</tr>
						</thead>
						<tbody>
							{events.map((e) => (
								<tr key={e.award}>
									<td className={styles.cell}>{e.event}</td>
									<td className={`${styles.cell} ${styles.award}`}>{e.award}</td>
									<td className={`${styles.cell} ${styles.value}`}>
										x{e.multiplier}
									</td>
								</tr>
							))}
						</tbody>
					</table>

					<div className={styles.foot}>
						Winning the biggest event of the year is worth 50 x 2 = 100 points.
						Golden Tickets are cosmetic and score nothing, and equal scores are
						separated by who holds the higher awards.
						{season && (
							<>
								{" "}
								{season.name} runs {toMonthNameDayAndYear(season.startDate)} to{" "}
								{toMonthNameDayAndYear(season.endDate)}.
							</>
						)}
					</div>
				</ModalBody>
			</ModalPanel>
		</CustomModal>
	);
}
